/**
 * storyText.js — THE shared text layer for a teller's own words (F-207 COMMIT 2).
 *
 * WHY THIS MODULE EXISTS. The punctuation repair used to live only inside `cleanContent`
 * (RemnantBookPDF.js), which the PDF and the chapter page preview call. The screen paths that render a
 * saved story — the keepsake/read view and the book reader — went through a LOCAL `cleanContent`
 * declared inside `renderStoryContent` (App.js) that stripped media tags and the Memory Interview
 * prefix but did NO punctuation repair, and shadowed the import by name. So `RemnantBookPDF.commaSpacing.test.js`
 * passed while the read view showed "it,I would say": the test exercised a function those paths never called.
 *
 * WHY NOT JUST CALL cleanContent FROM THE DISPLAY PATH (measured, not assumed): `cleanContent` also strips
 * markdown headers (`^#{1,6}`) and bold/italic markers (`**`, `*`, `_`). The display path feeds
 * `formatMarkdown`, which RENDERS those markers (`# ` → h2, `## ` → h3, `**bold**` → <strong>). Routing
 * display through the full `cleanContent` would silently delete every heading and bold run from the read
 * view. So the PUNCTUATION REPAIR is extracted here and shared, and each caller keeps the transforms its
 * own surface needs — one repair, called by every path that renders a teller's words.
 */

/**
 * repairPunctuationSpacing — restore a missing space after sentence-ending punctuation or a comma.
 * EXTRACTED VERBATIM from cleanContent (RemnantBookPDF.js) so print output is byte-identical; a drift
 * test pins that. Scoped to punctuation + LETTER so number grouping ("1,000", "25,000") and decimals
 * ("3.14") are untouched — those are punctuation + DIGIT.
 */
export function repairPunctuationSpacing(text) {
  if (!text) return '';
  let t = text;
  // MARKDOWN-TRANSPARENT (F-207). Markers must not hide a missing space. Found by the TEXT-PUNCT-1
  // guard: in "stove,**every morning**" the comma is followed by `*`, so a marker-blind repair does
  // nothing — and on the SCREEN the markers stay (formatMarkdown renders them), so the comma renders
  // glued to the bold word. Treat leading `*`/`_` markers as transparent on the lookahead.
  const MD = '[*_]{0,2}';
  // "rain.I was seven", "walls.When the sun"
  t = t.replace(new RegExp('([.!?])(' + MD + '[A-Z])', 'g'), '$1 $2');
  // closing quote then uppercase: ."He said
  t = t.replace(new RegExp('([.!?]["”\'])(' + MD + '[A-Z])', 'g'), '$1 $2');
  // F-94 D2: missing space after a COMMA before a letter — the live "it,I would say".
  t = t.replace(new RegExp(',(?=' + MD + '[A-Za-z])', 'g'), ', ');
  return t;
}

/**
 * prepareStoryDisplayText — the text-prep for SCREEN surfaces that render a saved story
 * (keepsake/read view, book reader). Strips what must not appear on screen, then applies the SHARED
 * punctuation repair. Markdown is deliberately PRESERVED for `formatMarkdown` to render.
 *
 * The two strip regexes are byte-identical to their counterparts inside `cleanContent`
 * (RemnantBookPDF.js lines for [audio|video] and the Memory Interview prefix), so the only behavioural
 * change versus the old inline code is the added punctuation repair.
 */
export function prepareStoryDisplayText(content) {
  if (!content) return '';
  let t = content.replace(/\n*\[(audio|video):[^\]]+\]/g, '');
  t = t.replace(/^\[Memory Interview[^\]]*\]\n+(?:Interviewed by:[^\n]*\n)?(?:Date:[^\n]*\n)?\n*/i, '');
  return repairPunctuationSpacing(t);
}
